import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable, switchMap, throwError } from 'rxjs';

import { ProjectFileModel } from '@models/files/project-file.model';
import { GenerateZipTimeEstimate } from '@models/files/zip-estimate.model';
import { ListResponse } from '@models/helpers';
import { BaseRestrictions } from '@models/module/fields/enums';
import { FileChunkParams, FileItemModel, SearchFileModel } from '@models/module/fields/main';
import {
  AddChunkedVersionParams,
  CreateFolderParams,
  DeleteItemsModel,
  DuplicateVersionBody,
  DuplicateVersionParams,
  EditFolderParams,
  FolderParentModel,
  GetFolderParentsParams,
  GetUploadUrlParams,
  GetVersionUrlParams,
  GlobalFilesModel,
  MoveItemsModel,
  RenameFileParams,
  RestoreItemsModel,
  SaveFileInfoBody,
  SaveFileInfoParams,
  ZipItemsModel,
} from '@module-instance/my-drive/common/models/request.models';
import { IFileService } from '@module-instance/my-drive/common/services/file-factory.service';
import { parseZipDownloadResponse } from '@module-instance/my-drive/common/utils/zip-download.utils';
import { DisplayFormats, TableColumn } from '@shared/components/data-list/common/models/table.interface';
import { FILE_SIZES } from '@shared/consts';
import { HttpService } from '@shared/services';
import { ContextStore } from '@signal-store/context.store';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class ProjectFileService implements IFileService<ProjectFileModel> {
  httpService = inject(HttpService);
  private http = inject(HttpClient);
  private readonly contextStore = inject(ContextStore);

  getFileUrl(fileId: number, versionId: number, isPreview?: boolean, streaming?: boolean) {
    const params = this.httpService.buildHttpParams({
      fileId,
      versionId,
      ...(isPreview ? { isPreview } : {}),
      ...(streaming ? { streaming } : {}),
    });
    return this.httpService
      .get('_api/ProjectFolderFile/GetFileURL', {
        httpOptions: { params },
      })
      .pipe(map((response: { url: string }) => response?.url));
  }

  getFilesUrl(versionIds: number[]) {
    return this.httpService.put<{ versionId: number; url: string }[]>('_api/ProjectFolderFile/GetFilesURL', {
      files: versionIds.map(versionId => ({ versionId })),
    });
  }

  getFile(
    fileId: number,
    parentId: number,
    entityInstanceId: number,
    versionId?: number,
  ): Observable<ProjectFileModel> {
    const params = this.httpService.buildHttpParams({ fileId, parentId, entityInstanceId, versionId });
    return this.httpService.get<ProjectFileModel>(`_api/ProjectFolderFile/GetFile`, {
      useLoader: false,
      httpOptions: { params },
    });
  }

  getFolderParents({
    folderId,
    entityInstanceId,
    folderEntityInstanceId,
    archiveId,
  }: GetFolderParentsParams): Observable<FolderParentModel[]> {
    const params = this.httpService.buildHttpParams({ entityInstanceId, folderId, folderEntityInstanceId, archiveId });
    return this.httpService.get<FolderParentModel[]>('_api/ProjectFolderFile/GetFolderParentsList', {
      useLoader: false,
      httpOptions: { params },
    });
  }

  searchFiles({ rootModuleId, ...body }: SearchFileModel) {
    const params = this.httpService.buildHttpParams({
      entityInstanceId: body.entityInstanceId,
      ...(rootModuleId ? { rootModuleId } : {}),
    });
    return this.httpService.post<ListResponse<FileItemModel>>('_api/ProjectFolderFile/Search', body, {
      httpOptions: { params: params },
      useLoader: false,
    });
  }

  getFileUploadUrl(body: GetUploadUrlParams) {
    const params = this.httpService.buildHttpParams({
      parentId: body.parentId,
      mimeType: body.mimeType,
      entityInstanceId: body.entityInstanceId,
      ...(body.isCreateVersion != null ? { isCreateVersion: body.isCreateVersion } : {}),
    });
    return environment.baseApiUrl + '_api/ProjectFolderFile/UploadFile?' + params.toString();
  }

  getFileNewVersionUrl(body: GetVersionUrlParams) {
    const params = this.httpService.buildHttpParams({
      fileId: body.fileId,
      mimeType: body.mimeType,
      entityInstanceId: body.entityInstanceId,
    });
    return environment.baseApiUrl + '_api/ProjectFolderFile/AddVersion?' + params.toString();
  }

  generateFileId() {
    return this.httpService
      .get<{ physicalFileId: string }>('_api/ProjectFolderFile/GenerateFileId', { useLoader: false })
      .pipe(map(response => response.physicalFileId));
  }

  uploadChunk(fileId: string, formData: FormData): Observable<void> {
    return this.httpService.post(`_api/ProjectFolderFile/UploadChunk?fileId=${fileId}`, formData, {
      useLoader: false,
      useErrorNotification: false,
    });
  }

  saveFileInfo(params: SaveFileInfoParams, body: SaveFileInfoBody) {
    const httpParams = this.httpService.buildHttpParams(params);
    return this.httpService.post<FileItemModel>('_api/ProjectFolderFile/SaveFileInfo', body, {
      useLoader: false,
      httpOptions: { params: httpParams },
    });
  }

  addChunkedVersion(params: AddChunkedVersionParams, body: SaveFileInfoBody) {
    const httpParams = this.httpService.buildHttpParams(params);
    return this.httpService.post<FileItemModel>('_api/ProjectFolderFile/AddChunkedVersion', body, {
      useLoader: false,
      httpOptions: { params: httpParams },
    });
  }

  renameFile(body: RenameFileParams): Observable<void> {
    const params = this.httpService.buildHttpParams({ entityInstanceId: body.entityInstanceId });
    return this.httpService.put(`_api/ProjectFolderFile/RenameFile`, body, {
      httpOptions: { params: params },
      useLoader: false,
    });
  }

  createFolder(body: CreateFolderParams): Observable<void> {
    const params = this.httpService.buildHttpParams({ entityInstanceId: body.entityInstanceId });
    return this.httpService.post(`_api/ProjectFolderFile/CreateFolder`, body, {
      httpOptions: { params: params },
      useLoader: false,
    });
  }

  editFolder(body: EditFolderParams): Observable<void> {
    const params = this.httpService.buildHttpParams({ entityInstanceId: body.entityInstanceId });
    return this.httpService.put(`_api/ProjectFolderFile/EditFolder`, body, {
      httpOptions: { params: params },
      useLoader: false,
    });
  }

  downloadFileFromUrl(url: string, filename?: string) {
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'file';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error downloading file from url', url, e);
    }
  }

  getChunkData({ file, start, end, chunks }: FileChunkParams) {
    const chunk = file.slice(start, end);
    const formData = new FormData();
    formData.append('file', chunk, file.name);
    formData.append('chunks', chunks.toString());
    formData.append('fileName', file.name);
    formData.append('_chunkSize', FILE_SIZES.chunkSize.toString());
    formData.append('_currentChunkSize', (end - start).toString());
    formData.append('_chunkNumber', Math.ceil(start / FILE_SIZES.chunkSize).toString());
    formData.append('_totalSize', file.size.toString());
    return formData;
  }

  duplicateVersion(params: DuplicateVersionParams, body: DuplicateVersionBody) {
    const httpParams = this.httpService.buildHttpParams(params);
    return this.httpService.post<number>('_api/ProjectFolderFile/DuplicateVersion', body, {
      useLoader: false,
      httpOptions: { params: httpParams },
    });
  }

  private notSupported(operation: string): Observable<never> {
    return throwError(() => new Error(`${operation} is not supported in Project Files.`));
  }

  shareFiles(): Observable<void> {
    return this.notSupported('shareFiles');
  }

  setFileAsUserAvatar(): Observable<void> {
    return this.notSupported('setFileAsUserAvatar');
  }

  setFileAsCompanyLogo(): Observable<void> {
    return this.notSupported('setFileAsCompanyLogo');
  }

  deleteSharesGlobal(): Observable<void> {
    return this.notSupported('deleteSharesGlobal');
  }

  deleteShares(): Observable<void> {
    return this.notSupported('deleteShares');
  }

  saveFile(body: Partial<ProjectFileModel>, entityInstanceId?: number): Observable<void> {
    const params = this.httpService.buildHttpParams({ entityInstanceId });
    return this.httpService.put(`_api/ProjectFolderFile/SaveFile`, body, {
      useLoader: false,
      httpOptions: { params },
    });
  }

  deleteItems(body: DeleteItemsModel, entityInstanceId?: number): Observable<void> {
    const params = this.httpService.buildHttpParams({ entityInstanceId });
    return this.httpService.put(`_api/ProjectFolderFile/Delete`, body, { useLoader: false, httpOptions: { params } });
  }

  permanentlyDeleteItems(body: DeleteItemsModel, entityInstanceId?: number): Observable<void> {
    const params = this.httpService.buildHttpParams({ entityInstanceId });
    return this.httpService.put(`_api/ProjectFolderFile/PermanentlyDelete`, body, {
      useLoader: false,
      httpOptions: { params },
    });
  }

  restoreItems(body: RestoreItemsModel, entityInstanceId?: number): Observable<void> {
    const params = this.httpService.buildHttpParams({ entityInstanceId });
    return this.httpService.put(`_api/ProjectFolderFile/Restore`, body, { useLoader: false, httpOptions: { params } });
  }

  unsetFilesGlobal(body: GlobalFilesModel, entityInstanceId?: number): Observable<void> {
    const params = this.httpService.buildHttpParams({ entityInstanceId });
    return this.httpService.put(`_api/ProjectFolderFile/UnSetFilesGlobal`, body, {
      useLoader: false,
      httpOptions: { params },
    });
  }

  moveItems(body: MoveItemsModel, entityInstanceId?: number): Observable<void> {
    const params = this.httpService.buildHttpParams({ entityInstanceId });
    return this.httpService.put(`_api/ProjectFolderFile/Move`, body, { httpOptions: { params } });
  }

  downloadZip(
    body: ZipItemsModel,
    entityInstanceId?: number,
  ): Observable<HttpResponse<Blob> | GenerateZipTimeEstimate> {
    const query = entityInstanceId ? `?entityInstanceId=${entityInstanceId}` : '';
    return this.http
      .put(environment.baseApiUrl + '_api/ProjectFolderFile/DownloadZipFromFolderAndFiles' + query, body, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(switchMap(parseZipDownloadResponse));
  }

  getTableColumns(): TableColumn[] {
    return [
      {
        id: 0,
        name: 'image',
        displayName: '',
        srOnlyLabel: 'a11y.fileExplorer.columnPreview',
        priority: 3,
        sortable: false,
        width: 0.5,
        align: 'center',
        displayFormatId: DisplayFormats.Image,
      },
      {
        id: 1,
        name: 'name',
        displayName: 'fileExplorer.name',
        priority: 3,
        sortable: true,
        width: 3,
      },
      {
        id: 2,
        name: 'createdBy',
        displayName: 'fileExplorer.createdBy',
        priority: 3,
        sortable: true,
        width: 1,
      },
      {
        id: 3,
        name: 'createdOn',
        displayName: 'fileExplorer.createdOn',
        priority: 2,
        sortable: true,
        displayFormatId: DisplayFormats.Date,
        restrictions: BaseRestrictions.DateTime,
        width: 1,
      },
      {
        id: 4,
        name: 'updatedOn',
        displayName: 'fileExplorer.updatedOn',
        priority: 0,
        sortable: true,
        displayFormatId: DisplayFormats.Date,
        restrictions: BaseRestrictions.DateTime,
        width: 1,
      },
      {
        id: 5,
        name: 'shared',
        displayName: 'fileExplorer.shared',
        priority: 1,
        sortable: false,
        width: 0.7,
        align: 'center',
        displayFormatId: DisplayFormats.Image,
      },
      {
        id: 6,
        name: 'fileSizeDisplay',
        displayName: 'fileDialog.fileSize',
        priority: 1,
        width: 1,
        sortable: true,
      },
    ];
  }

  getSearchColumns(isGlobal: boolean): TableColumn[] {
    // Legacy also drops the Shared flag from the search sets (filecontractmanagerController.js:85-101).
    const columns = this.getTableColumns().filter(column => column.name !== 'shared');
    const resultColumns: TableColumn[] = [
      {
        id: 7,
        name: 'folderPath',
        displayName: 'fileExplorer.folder',
        priority: 3,
        sortable: false,
        width: 2,
      },
    ];
    if (isGlobal) {
      resultColumns.push({
        id: 8,
        name: 'contractName',
        displayName: this.contextStore.contractsName(),
        priority: 2,
        sortable: false,
        width: 1.5,
      });
    }
    columns.splice(columns.findIndex(column => column.name === 'name') + 1, 0, ...resultColumns);
    return columns;
  }

  getRecycleBinColumns(): TableColumn[] {
    return [
      {
        id: 0,
        name: 'image',
        displayName: '',
        srOnlyLabel: 'a11y.fileExplorer.columnPreview',
        priority: 3,
        sortable: false,
        width: 0.5,
        align: 'center',
        displayFormatId: DisplayFormats.Image,
      },
      {
        id: 1,
        name: 'name',
        displayName: 'fileExplorer.name',
        priority: 3,
        sortable: true,
        width: 3,
      },
      {
        id: 2,
        name: 'updatedBy',
        displayName: 'fileExplorer.deletedBy',
        priority: 3,
        sortable: true,
        width: 1,
      },
      {
        id: 3,
        name: 'updatedOn',
        displayName: 'fileExplorer.deletedOn',
        priority: 2,
        sortable: true,
        displayFormatId: DisplayFormats.Date,
        restrictions: BaseRestrictions.DateTime,
        width: 1,
      },
      {
        id: 4,
        name: 'pathWhenDeleted',
        displayName: 'fileExplorer.originalLocation',
        priority: 2,
        sortable: false,
        width: 2,
      },
      {
        id: 5,
        name: 'fileSizeDisplay',
        displayName: 'fileDialog.fileSize',
        priority: 1,
        width: 1,
        sortable: true,
      },
    ];
  }
}
